import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Art, Mascot } from "./Art";

/** Pastel tones used across the remake: [card background, accent text, chip]. Dark mode is handled in index.css (.pastel-*). */
export const PASTEL = ["pastel-blue", "pastel-pink", "pastel-green", "pastel-yellow", "pastel-purple", "pastel-orange"] as const;
export const pastel = (i: number) => PASTEL[i % PASTEL.length]!;

/** Page header: big title, one line of help, mascot on the right. */
export function Hero({ title, text, pose, children }: { title: string; text?: string; pose: string; children?: ReactNode }) {
  return (
    <section className="pastel-blue mb-6 flex flex-col-reverse items-center gap-3 overflow-hidden rounded-[28px] p-5 text-center sm:flex-row sm:p-8 sm:text-left">
      <div className="min-w-0 flex-1">
        <h1 className="text-2xl font-black leading-tight tracking-tight text-slate-900 sm:text-4xl">{title}</h1>
        {text && <p className="mt-2 max-w-xl text-base text-slate-600 sm:text-lg">{text}</p>}
        {children && <div className="mt-4 flex flex-wrap justify-center gap-3 sm:justify-start">{children}</div>}
      </div>
      <Mascot pose={pose} className="h-36 w-36 shrink-0 sm:h-48 sm:w-48" />
    </section>
  );
}

const OUTLINE = { WebkitTextStroke: "6px #fff", paintOrder: "stroke fill", textShadow: "0 4px 0 rgb(30 64 175 / .3)" } as const;

/** Wide picture banner (16:9 cover with the two children at the sides): the page title sits on the empty middle, as in the PDF. */
export function Banner({ art, kicker, title, tagline }: { art: string; kicker?: string; title: string; tagline?: string }) {
  return (
    <>
    <section className="relative mb-4 overflow-hidden rounded-[28px] bg-sky-200 shadow-md">
      <Art id={art} alt="" className="h-60 w-full object-cover sm:h-72 md:h-80" />
      <div className="absolute inset-0 grid place-items-center px-20 text-center sm:px-40">
        <div>
          {kicker && <p className="text-xl font-black text-sky-500 sm:text-4xl" style={OUTLINE}>{kicker}</p>}
          <h1 className="-mt-1 text-3xl font-black text-amber-400 sm:text-6xl" style={OUTLINE}>{title}</h1>
          {tagline && <span className="mt-1 hidden max-w-full rounded-full bg-pink-500 px-4 py-1 text-base font-extrabold text-white shadow sm:inline-block">{tagline}</span>}
        </div>
      </div>
    </section>
    {tagline && <p className="relative z-10 -mt-7 mb-4 text-center sm:hidden"><span className="inline-block max-w-[90%] rounded-full bg-pink-500 px-4 py-1.5 text-xs font-extrabold text-white shadow-md ring-2 ring-white">{tagline}</span></p>}
    </>
  );
}

/** Square-ish cover on the right, title and text on the left (for 4:3 covers). */
export function SplitHero({ art, title, text, children }: { art: string; title: string; text?: string; children?: ReactNode }) {
  return (
    <section className="pastel-blue mb-6 grid items-center gap-3 overflow-hidden rounded-[28px] md:grid-cols-2">
      <div className="p-5 text-center sm:p-8 md:text-left">
        <h1 className="text-3xl font-black leading-tight tracking-tight text-slate-900 sm:text-5xl">{title}</h1>
        {text && <p className="mt-2 text-base text-slate-600 sm:text-lg">{text}</p>}
        {children && <div className="mt-4 flex flex-wrap justify-center gap-3 md:justify-start">{children}</div>}
      </div>
      <Art id={art} alt="" className="h-56 w-full object-cover sm:h-72 md:h-full" />
    </section>
  );
}

/** A coloured stat tile with a drawn icon. */
export function StatTile({ icon, value, label, tone }: { icon: ReactNode; value: ReactNode; label: string; tone: string }) {
  return (
    <div className={`${tone} flex items-center gap-3 rounded-3xl p-3 shadow-sm`}>
      <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-white text-brand shadow-sm">{icon}</span>
      <div><p className="text-2xl font-black leading-none text-slate-900">{value}</p><p className="mt-1 text-xs font-bold text-slate-600">{label}</p></div>
    </div>
  );
}

const BTN = "inline-flex min-h-12 items-center justify-center rounded-full px-6 py-2.5 text-base font-extrabold shadow-sm transition active:scale-95";
export function BigLink({ to, children, tone = "primary" }: { to: string; children: ReactNode; tone?: "primary" | "soft" }) {
  return <Link to={to} className={`${BTN} ${tone === "primary" ? "bg-brand text-white hover:bg-brand-dark" : "bg-white/80 text-brand ring-1 ring-brand/20 hover:bg-white dark:bg-slate-100 dark:text-brand"}`}>{children}</Link>;
}
export function BigButton({ onClick, children, disabled, tone = "primary" }: { onClick: () => void; children: ReactNode; disabled?: boolean; tone?: "primary" | "green" | "soft" }) {
  const cls = tone === "green" ? "bg-emerald-500 text-white hover:bg-emerald-600" : tone === "soft" ? "bg-white/80 text-brand ring-1 ring-brand/20 hover:bg-white dark:bg-slate-100" : "bg-brand text-white hover:bg-brand-dark";
  return <button type="button" onClick={onClick} disabled={disabled} className={`${BTN} ${cls} disabled:opacity-50`}>{children}</button>;
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return <h2 className="mb-3 mt-8 text-xl font-black tracking-tight text-slate-900">{children}</h2>;
}
